import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  getAllEngagements,
  getEngagementById,
  getAllLedgerEntrys,
  updateLedgerEntry,
  createPaymentReceipt,
  createAuditLog,
} from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { CheckCircle, AlertTriangle, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Drawer from '../../components/ui/Drawer';
import Modal from '../../components/ui/Modal';
import PaymentReceiptForm from '../../components/forms/PaymentReceiptForm';
import { LEDGER_APPROVAL, LEDGER_CATEGORY, LEDGER_STATUS, LEDGER_TYPE, formatDate, formatINR, label, options } from '../../constants/garmentCodes';

const typeColors = {
  OPENING: 'bg-[#F4F3F0] text-[#5C5A54]',
  RECHARGE: 'bg-[#E8F5EE] text-[#1A6B3E]',
  DEBIT: 'bg-[#FDECEA] text-[#C0222B]',
  HOLD: 'bg-[#FEF3E2] text-[#92560C]',
  RELEASE: 'bg-[#E8F5EE] text-[#1A6B3E]',
  REVERSAL: 'bg-[#F0EBFF] text-[#5B21B6]',
  ADJUSTMENT: 'bg-[#FEF3E2] text-[#92560C]',
};
const APPROVAL = { PENDING: 2, PENDING_ACK: 3, ACKNOWLEDGED: 4 };

export default function BrandWallet() {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const engagementId = params.get('engagementId') || '';
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [ackTxn, setAckTxn] = useState(null);
  const [ackNote, setAckNote] = useState('');
  const [receiptOpen, setReceiptOpen] = useState(false);

  const engagements = useSelector((state) => state.engagement.engagements);
  const engagement = useSelector((state) => state.engagement.engagement);
  const entries = useSelector((state) => state.ledgerentry.entries);
  const loading = useSelector((state) => state.ledgerentry.loading);
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);
  const companyId = useSelector((state) => state.auth?.companyId);
  const brandId = useSelector((state) => state.brand.profile?.id);

  useEffect(() => {
    if (!brandId) return;
    dispatch(
      getAllEngagements({
        brandId,
        callback: {
          success: (list) => {
            if (!engagementId && list.length) setParams({ engagementId: String(list[0].id) }, { replace: true });
          },
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, brandId]);

  useEffect(() => {
    if (engagementId) dispatch(getEngagementById({ id: engagementId }));
  }, [dispatch, engagementId]);

  useEffect(() => {
    dispatch(getAllLedgerEntrys({ engagementId, type: typeFilter }));
  }, [dispatch, engagementId, typeFilter]);

  const totals = entries.reduce(
    (acc, t) => {
      acc.credits += Number(t.credit) || 0;
      acc.debits += Number(t.debit) || 0;
      return acc;
    },
    { credits: 0, debits: 0 },
  );
  const threshold = engagement?.lowBalanceThreshold;
  const low = threshold != null && (engagement?.availableBalance ?? 0) < threshold;

  const acknowledge = () => {
    const txn = ackTxn;
    dispatch(
      updateLedgerEntry({
        payload: { id: txn.id, approvalStatus: APPROVAL.ACKNOWLEDGED },
        callback: {
          success: () =>
            dispatch(
              createAuditLog({
                payload: {
                  companyId,
                  userId,
                  action: 'Ledger entry acknowledged',
                  entityType: 'LedgerEntry',
                  entityId: txn.id,
                  engagementId: txn.engagementId,
                  summary: ackNote.trim() || `Acknowledged ${txn.reference || `entry #${txn.id}`}`,
                  beforeState: { approvalStatus: txn.approvalStatus },
                  afterState: { approvalStatus: APPROVAL.ACKNOWLEDGED },
                },
              }),
            ),
        },
      }),
    );
    setAckTxn(null);
    setAckNote('');
  };

  const submitReceipt = (body) => {
    dispatch(createPaymentReceipt({ payload: body }));
    setReceiptOpen(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        title="Wallets & Ledger"
        subtitle={engagement ? `${engagement.brandName || ''} × ${engagement.manufacturerName || ''}` : ''}
        actions={<Button variant="primary" size="sm" onClick={() => setReceiptOpen(true)}>Submit receipt</Button>}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-[#5C5A54]">Engagement</label>
          <select value={engagementId} onChange={(e) => setParams({ engagementId: e.target.value })} className="text-sm border border-[#E5E3DE] rounded-lg px-3 py-1.5 bg-white outline-none">
            {!engagements.length && <option value="">No engagements</option>}
            {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#1E2A3B] rounded-xl p-5 text-white">
            <p className="text-[10px] font-medium text-white/60 uppercase tracking-wider">Current Balance</p>
            <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatINR(engagement?.walletBalance)}</p>
            <p className="text-xs text-white/60 mt-1">Total credits minus total debits</p>
            <p className="text-xs text-white/50 mt-3">Last activity {formatDate(engagement?.lastActivity)}</p>
          </div>
          <div className="bg-[#FEF3E2] border border-[#F7C97A] rounded-xl p-5">
            <p className="text-[10px] font-medium text-[#92560C] uppercase tracking-wider">Held / Committed</p>
            <p className="text-3xl font-bold text-[#92560C] mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatINR(engagement?.heldAmount)}</p>
            <p className="text-xs text-[#92560C]/80 mt-1">Approved but not yet billed</p>
          </div>
          <div className={`rounded-xl p-5 border ${low ? 'bg-[#FDECEA] border-[#F5A9AC]' : 'bg-[#E8F5EE] border-[#A8DEC0]'}`}>
            <p className={`text-[10px] font-medium uppercase tracking-wider ${low ? 'text-[#C0222B]' : 'text-[#1A6B3E]'}`}>Available Balance</p>
            <p className={`text-3xl font-bold mt-2 ${low ? 'text-[#C0222B]' : 'text-[#1A6B3E]'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{formatINR(engagement?.availableBalance)}</p>
            {threshold != null && (
              <div className="flex items-center gap-1.5 mt-1">
                <AlertTriangle size={11} className={low ? 'text-[#C0222B]' : 'text-[#92560C]'} />
                <p className={`text-xs ${low ? 'text-[#C0222B]' : 'text-[#92560C]'}`}>{low ? `Below ${formatINR(threshold)} threshold` : 'Threshold maintained'}</p>
              </div>
            )}
            <p className="text-xs text-[#1A6B3E]/70 mt-2">Controls all new chargeable work</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
          {[
            { label: 'Total Credits', value: formatINR(totals.credits), icon: ArrowUpRight, color: 'text-[#1A6B3E]' },
            { label: 'Total Debits', value: formatINR(totals.debits), icon: ArrowDownRight, color: 'text-[#C0222B]' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-[#E5E3DE] p-4 flex items-center gap-3">
              <s.icon size={16} className={s.color} />
              <div>
                <p className="text-[10px] text-[#9B9893] uppercase tracking-wider">{s.label}</p>
                <p className="text-base font-bold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <button onClick={() => setTypeFilter('')} className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${typeFilter === '' ? 'bg-[#1E2A3B] text-white border-transparent' : 'bg-white text-[#5C5A54] border-[#E5E3DE] hover:bg-[#F4F3F0]'}`}>All</button>
          {options(LEDGER_TYPE).map((o) => (
            <button key={o.value} onClick={() => setTypeFilter(String(o.value))} className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${typeFilter === String(o.value) ? 'bg-[#1E2A3B] text-white border-transparent' : 'bg-white text-[#5C5A54] border-[#E5E3DE] hover:bg-[#F4F3F0]'}`}>
              {o.label}
            </button>
          ))}
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Ledger_Transactions"
            columns={[
              { id: 'date', label: 'DATE', align: 'left' },
              { id: 'type', label: 'TYPE', align: 'left' },
              { id: 'description', label: 'DESCRIPTION', align: 'left', minWidth: 200 },
              { id: 'reference', label: 'REFERENCE', align: 'left' },
              { id: 'credit', label: 'CREDIT', align: 'left' },
              { id: 'debit', label: 'DEBIT', align: 'left' },
              { id: 'held', label: 'HELD', align: 'left' },
              { id: 'balance', label: 'BALANCE', align: 'left' },
              { id: 'available', label: 'AVAILABLE', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'action', label: 'ACTIONS', align: 'center', disableFilter: true },
            ]}
            rows={entries.map((txn) => {
              const type = label(LEDGER_TYPE, txn.type);
              const open = () => setSelectedTxn(txn);
              return {
                id: txn.id,
                rColumns: [
                  <div key="date" label={txn.date || ''} onClick={open} className="cursor-pointer text-xs text-[#5C5A54] whitespace-nowrap">{formatDate(txn.date)}</div>,
                  <div key="type" label={type} onClick={open} className="cursor-pointer">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${typeColors[type] || typeColors.OPENING}`}>{type}</span>
                  </div>,
                  <div key="desc" label={txn.description || ''} onClick={open} className="cursor-pointer max-w-[200px]">
                    <p className="text-xs text-[#1A1915] truncate">{txn.description || '—'}</p>
                    {txn.linkedItemType && <p className="text-[10px] text-[#9B9893] font-mono">{txn.linkedItemType} #{txn.linkedItemId}</p>}
                  </div>,
                  <div key="ref" label={txn.reference || ''} onClick={open} className="cursor-pointer font-mono text-xs text-[#9B9893]">{txn.reference || '—'}</div>,
                  <div key="cr" label={txn.credit ? formatINR(txn.credit) : ''} onClick={open} className="cursor-pointer text-xs font-semibold text-[#1A6B3E]">{txn.credit ? `+${formatINR(txn.credit)}` : '—'}</div>,
                  <div key="db" label={txn.debit ? formatINR(txn.debit) : ''} onClick={open} className="cursor-pointer text-xs font-semibold text-[#C0222B]">{txn.debit ? `-${formatINR(txn.debit)}` : '—'}</div>,
                  <div key="held" label={txn.held ? formatINR(txn.held) : ''} onClick={open} className="cursor-pointer text-xs font-semibold text-[#92560C]">{txn.held ? formatINR(txn.held) : '—'}</div>,
                  <div key="bal" label={formatINR(txn.runningBalance)} onClick={open} className="cursor-pointer text-xs font-medium text-[#1A1915]">{txn.runningBalance != null ? formatINR(txn.runningBalance) : '—'}</div>,
                  <div key="avail" label={formatINR(txn.availableBalance)} onClick={open} className="cursor-pointer text-xs font-medium text-[#3D52A0]">{txn.availableBalance != null ? formatINR(txn.availableBalance) : '—'}</div>,
                  <div key="stat" label={label(LEDGER_APPROVAL, txn.approvalStatus)}>
                    {txn.approvalStatus === APPROVAL.PENDING_ACK ? (
                      <button onClick={() => setAckTxn(txn)} className="text-[10px] font-semibold text-[#92560C] bg-[#FEF3E2] px-2 py-1 rounded-full border border-[#F7C97A] hover:bg-[#FDE68A]">Acknowledge</button>
                    ) : txn.approvalStatus === APPROVAL.PENDING ? (
                      <Badge variant="amber">Pending</Badge>
                    ) : (
                      <Badge variant="green">{label(LEDGER_STATUS, txn.status, 'Recorded')}</Badge>
                    )}
                  </div>,
                  <div key="act" label="" onClick={open} className="cursor-pointer w-full h-full flex items-center justify-center"><FileText size={13} className="text-[#9B9893]" /></div>,
                ],
              };
            })}
          />
          {!loading && entries.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No transactions</p>}
        </div>
      </div>

      <Drawer open={!!selectedTxn} onClose={() => setSelectedTxn(null)} title="Transaction Details" subtitle={selectedTxn?.reference} width="w-[450px]">
        {selectedTxn && (
          <div className="bg-[#FAFAF8] p-4 rounded-xl space-y-2 text-xs">
            {[
              ['Reference', selectedTxn.reference],
              ['Date', formatDate(selectedTxn.date)],
              ['Posting Date', formatDate(selectedTxn.postingDate)],
              ['Type', label(LEDGER_TYPE, selectedTxn.type)],
              ['Category', label(LEDGER_CATEGORY, selectedTxn.category)],
              ['Description', selectedTxn.description],
              ['Credit', selectedTxn.credit ? formatINR(selectedTxn.credit) : null],
              ['Debit', selectedTxn.debit ? formatINR(selectedTxn.debit) : null],
              ['Held', selectedTxn.held ? formatINR(selectedTxn.held) : null],
              ['Released', selectedTxn.released ? formatINR(selectedTxn.released) : null],
              ['Running Balance', selectedTxn.runningBalance != null ? formatINR(selectedTxn.runningBalance) : null],
              ['Available Balance', selectedTxn.availableBalance != null ? formatINR(selectedTxn.availableBalance) : null],
              ['Status', label(LEDGER_STATUS, selectedTxn.status)],
              ['Approval', label(LEDGER_APPROVAL, selectedTxn.approvalStatus)],
              ['Linked Item', selectedTxn.linkedItemType ? `${selectedTxn.linkedItemType} #${selectedTxn.linkedItemId}` : null],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <span className="text-[#9B9893]">{k}</span>
                <span className="font-medium text-right">{v ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
      </Drawer>

      <Modal open={receiptOpen} onClose={() => setReceiptOpen(false)} title="Submit Payment Receipt" size="md">
        <PaymentReceiptForm engagements={engagements} defaultEngagementId={engagementId} userId={userId} onSubmit={submitReceipt} onCancel={() => setReceiptOpen(false)} />
      </Modal>

      <Modal
        open={!!ackTxn}
        onClose={() => setAckTxn(null)}
        title="Acknowledge Entry"
        subtitle={ackTxn?.reference}
        size="sm"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={() => setAckTxn(null)}>Cancel</Button>
            <Button variant="success" size="md" icon={<CheckCircle size={14} />} onClick={acknowledge}>Confirm acknowledgement</Button>
          </div>
        }
      >
        {ackTxn && (
          <div className="space-y-4">
            <div className="bg-[#FAFAF8] rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#9B9893]">Description</span><span className="font-medium text-[#1A1915]">{ackTxn.description || '—'}</span></div>
              <div className="flex justify-between"><span className="text-[#9B9893]">Amount</span><span className="font-semibold text-[#1A1915]">{formatINR(ackTxn.debit || ackTxn.credit || ackTxn.held)}</span></div>
              <div className="flex justify-between"><span className="text-[#9B9893]">Date</span><span className="font-medium text-[#1A1915]">{formatDate(ackTxn.date)}</span></div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5A54]">Optional note</label>
              <textarea value={ackNote} onChange={(e) => setAckNote(e.target.value)} rows={3} className="w-full mt-1.5 px-3 py-2 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0] resize-none" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
